/**
 * Turns one Firestore scan document into the shape the report renders.
 *
 * Two jobs, both of which were previously done inline in the page and neither
 * of which is presentation:
 *
 *  1. RECONCILING SCAN VERSIONS. Scans exist from before the multi-view
 *     rollout, carrying a single front-view `postureData` and a flat
 *     `imageUrls` list. Those links are in patients' inboxes and have to keep
 *     working, so an old document is adapted into the new shape here rather
 *     than branching the whole page on the document's age.
 *
 *  2. FINDING THE FINDINGS. A report that lists forty measurements and leaves
 *     the reader to spot the four that matter has published data, not a
 *     report. `collectFindings` pulls out everything outside its normal range,
 *     worst first, so the document can lead with it.
 *
 * Nothing here decides what is normal — every verdict comes from the scan or
 * from metricBands.js.
 */

import {
  NORMAL_RANGES, rangeForAngle, severityCutsFor, severityForAngle, severityRank,
} from '../metricBands';

export const HEEL_TEXT = {
  normal: 'Normal',
  valgus: 'Valgus — heel tilts inward',
  varus: 'Varus — heel tilts outward',
};

export const SHIN_TEXT = {
  neutral: 'Neutral alignment',
  'internal-rotation': 'Internal rotation of the lower leg',
  'external-rotation': 'External rotation of the lower leg',
};

const POSE_LABELS = {
  stand: 'Standing — full weight bearing',
  'knee-bend': 'Knees bent — dynamic load',
  'toe-up': 'Toes raised — Achilles tracking',
};

const VIEW_LABELS = {
  front: 'Front view',
  side: 'Side view',
  back: 'Back view',
};

const LEG_CAPTIONS = ['Legs — standing', 'Legs — knees bent', 'Legs — toes raised'];

function nonEmpty(value) {
  return value !== undefined && value !== null && value !== '';
}

/** The patient's name, however this document happens to carry it. */
export function patientNameOf(scan) {
  const joined = `${scan.firstName ?? ''} ${scan.lastName ?? ''}`.trim();
  return scan.patientName || joined || 'Patient';
}

export function formatScanDate(timestamp) {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Every analysed body view, old single-view documents included. */
export function postureViewsOf(scan) {
  if (Array.isArray(scan.postureViews) && scan.postureViews.length) {
    return scan.postureViews.map(v => ({ ...v, label: v.label ?? VIEW_LABELS[v.view] ?? v.view }));
  }
  if (!scan.postureData) return [];
  const d = scan.postureData;
  return [{
    view: 'front',
    label: VIEW_LABELS.front,
    angles: d.angles ?? [],
    offsets: d.offsets ?? [],
    deviations: d.deviations ?? [],
    summary: d.summary ?? '',
    score: d.score ?? null,
    landmarksEdited: d.landmarksEdited ?? false,
    landmarksSeeded: d.landmarksSeeded ?? false,
    scaleSource: d.scaleSource ?? null,
    overlayUrl: scan.postureOverlayUrl ?? null,
  }];
}

/**
 * A capture whose markers are still the kiosk's fixed PLACEHOLDERS: the joint
 * detector could not read it and nobody placed the markers. Its image shows
 * dots at arbitrary positions and it carries no measurement, so the report
 * must say "not measured" rather than show it as a reading.
 */
export function isUnmeasured(capture) {
  return Boolean(capture?.landmarksSeeded) && !capture?.landmarksEdited;
}

/** Every analysed leg pose, old single-pose documents included. */
export function legPosesOf(scan) {
  if (Array.isArray(scan.legPoses) && scan.legPoses.length) {
    return scan.legPoses.map(p => ({ ...p, label: p.label ?? POSE_LABELS[p.pose] ?? p.pose }));
  }
  if (!scan.legData) return [];
  return [{
    pose: 'stand',
    label: POSE_LABELS.stand,
    angles: scan.legData.angles ?? [],
    findings: scan.legData.findings ?? null,
    landmarksEdited: scan.legData.landmarksEdited ?? false,
    landmarksSeeded: scan.legData.landmarksSeeded ?? false,
    overlayUrl: scan.legOverlayUrl ?? null,
  }];
}

/**
 * Pressure maps with their provenance intact.
 *
 * A sensor map is a measurement and a camera map is an optical estimate from
 * the podoscope image. The older documents carry only a URL list, and those are
 * all sensor frames — that list was written before the camera maps existed.
 */
export function pressureMapsOf(scan) {
  // The camera's pressure picture is the plantar view; a map of a later
  // capture (the dorsal, now toe up, view of older scans) is left out.
  if (Array.isArray(scan.heatmaps) && scan.heatmaps.length) {
    return scan.heatmaps.filter(m => m?.source !== 'camera' || !/dorsal|toe up/i.test(m.label ?? ''));
  }
  return (scan.heatmapUrls ?? []).map((url, i) => ({
    url,
    source: 'sensor',
    label: i === 0 ? 'Left foot' : i === 1 ? 'Right foot' : `Pressure map ${i + 1}`,
  }));
}

/** The operator's choice of plantar picture (kiosk lib/reportImages): 'heatmap', 'photo' or 'both'. */
export function reportImagesOf(scan) {
  const v = scan?.reportImages;
  return v === 'heatmap' || v === 'photo' ? v : 'both';
}

/**
 * The pressure maps and foot photos the report shows, as the operator chose.
 *
 * "photo" shows the captured plantar image instead of the camera's heatmap,
 * "heatmap" the heatmap instead of the plantar image, "both" both. A sensor
 * mat's maps are measurements and always shown, and so is the toe up photo,
 * which has no heatmap. A choice that would leave the plantar view with no
 * picture at all (its photo or its map never uploaded) shows what there is.
 *
 * Each photo keeps its capture `index` (0 plantar, 1 toe up): the report
 * names and describes it by that.
 */
export function plantarImageryOf(scan) {
  const maps = pressureMapsOf(scan);
  const photos = (Array.isArray(scan.scanImageUrls) ? scan.scanImageUrls : [])
    .slice(0, 2).map((url, index) => ({ url, index })).filter(p => p.url);
  const choice = reportImagesOf(scan);
  const plantarPhoto = photos.some(p => p.index === 0);
  const cameraMap = maps.some(m => m.source === 'camera');
  return {
    choice,
    pressure: choice === 'photo' && plantarPhoto ? maps.filter(m => m.source !== 'camera') : maps,
    footPhotos: choice === 'heatmap' && cameraMap ? photos.filter(p => p.index !== 0) : photos,
  };
}

/**
 * The eyes-closed balance test, or null if the session never ran one.
 *
 * Images and measurement are independent here, exactly as they are on the
 * kiosk. `metrics` is null on a burst that lost too many frames, and
 * `inconclusive` then carries the sentence explaining why — the section prints
 * the pictures and says the sway could not be measured, rather than printing a
 * number derived from a fragment.
 *
 * `shotUrls` and `traceUrl` are written in a second pass after the images
 * upload, so a document can legitimately carry the measurement with no pictures
 * at all: Storage was unreachable, the finding survived. Read defensively.
 */
export function balanceTestOf(scan) {
  const bt = scan.balanceTest;
  if (!bt || !bt.capturedAt) return null;

  const shots = Array.isArray(bt.shotUrls) ? bt.shotUrls.filter(Boolean) : [];
  const metrics = bt.metrics ?? null;

  // Nothing to show and nothing to say: not a test that went badly, but a
  // field left over from a partial write. Printing a heading for it would put
  // an empty numbered section in the middle of the report.
  if (!shots.length && !metrics && !bt.inconclusive) return null;

  return {
    capturedAt: bt.capturedAt,
    shots,
    expectedShots: typeof bt.expectedShots === 'number' ? bt.expectedShots : shots.length,
    traceUrl: bt.traceUrl ?? null,
    metrics,
    inconclusive: bt.inconclusive ?? null,
  };
}

/**
 * The capture gallery, captioned by what each photograph actually is.
 *
 * The flat `imageUrls` fallback is captioned by position only, because that is
 * all an old document records — it is better to say "Capture 3" than to assert
 * a body view for a photograph of a leg.
 */
export function galleryOf(scan) {
  const typed = scan.postureImageUrls || scan.legImageUrls || scan.scanImageUrls;
  if (!typed) {
    return (scan.imageUrls ?? []).map((url, i) => ({ url, caption: `Capture ${i + 1}` }));
  }
  return [
    ...(scan.postureImageUrls ?? []).map((url, i) => ({ url, caption: i === 0 ? 'Body — front' : 'Body — side' })),
    ...(scan.legImageUrls ?? []).map((url, i) => ({ url, caption: LEG_CAPTIONS[i] ?? `Legs ${i + 1}` })),
    ...(scan.scanImageUrls ?? []).map((url, i) => ({ url, caption: i === 0 ? 'Foot — plantar' : 'Foot — toe up' })),
  ];
}

/**
 * Every measurement that fell outside its normal range, worst first.
 *
 * Ties break on how far past the range the reading is, so two "Moderate"
 * findings are not ordered by whichever view happened to be captured first.
 */
export function collectFindings(views, poses) {
  const out = [];

  const consider = (angle, context) => {
    if (!angle || typeof angle.degrees !== 'number') return;
    const severity = severityForAngle(angle);
    if (severity === 'normal') return;
    const range = rangeForAngle(angle);
    const excess = range
      ? (angle.degrees < range[0] ? range[0] - angle.degrees : Math.max(0, angle.degrees - range[1]))
      : 0;
    out.push({ name: angle.name, degrees: angle.degrees, range, severity, context, excess });
  };

  views.forEach(v => (v.angles ?? []).forEach(a => consider(a, v.label ?? v.view)));
  poses.forEach(p => (p.angles ?? []).forEach(a => consider(a, p.label ?? p.pose)));

  return out.sort((a, b) =>
    severityRank(b.severity) - severityRank(a.severity) || b.excess - a.excess
  );
}

/**
 * How many measurements the session produced.
 *
 * An empty findings list means two very different things — everything measured
 * came back normal, or nothing was measured at all — and only one of them is
 * reassuring. The report must be able to tell them apart before it prints
 * "within normal limits" over a scan that measured nothing.
 */
export function measuredCount(views, poses) {
  const count = list => list.reduce((n, item) => n + (item.angles?.length ?? 0), 0);
  return count(views) + count(poses);
}

/** The worst severity anywhere in the report, for the header verdict. */
export function worstSeverity(findings) {
  return findings.reduce(
    (worst, f) => (severityRank(f.severity) > severityRank(worst) ? f.severity : worst),
    'normal'
  );
}

/**
 * A leg finding as a pin on a bilateral bar.
 *
 * `side` is which direction label the reading sits under: heel varus is
 * inversion (left of centre) and valgus eversion (right); shin internal
 * rotation is tibial torsion (left) and external is fibular (right). The
 * severity is graded on the same range the bar draws, so pin and band agree.
 */
export function heelSide(finding) {
  if (!finding) return null;
  const range = NORMAL_RANGES['Left calcaneal angle'];
  return {
    degrees: finding.degrees,
    side: finding.finding === 'normal' ? 0 : finding.finding === 'varus' ? -1 : 1,
    severity: severityForAngle({ name: 'Left calcaneal angle', degrees: finding.degrees, normalRange: range }),
    text: HEEL_TEXT[finding.finding] ?? finding.finding,
    confirmed: finding.source === 'clinician',
  };
}

export function shinSide(finding) {
  if (!finding) return null;
  const range = NORMAL_RANGES['Left shin rotation'];
  return {
    degrees: finding.degrees,
    side: finding.finding === 'neutral' ? 0 : finding.finding === 'internal-rotation' ? -1 : 1,
    severity: severityForAngle({ name: 'Left shin rotation', degrees: finding.degrees, normalRange: range }),
    text: SHIN_TEXT[finding.finding] ?? finding.finding,
    confirmed: finding.source === 'clinician',
  };
}

export const HEEL_RANGE = NORMAL_RANGES['Left calcaneal angle'];
export const SHIN_RANGE = NORMAL_RANGES['Left shin rotation'];
export const HEEL_CUTS = severityCutsFor('Left calcaneal angle');
export const SHIN_CUTS = severityCutsFor('Left shin rotation');

export function hasFindings(pose) {
  const f = pose?.findings;
  return Boolean(f && (f.heelLeft || f.heelRight || f.shinLeft || f.shinRight));
}

/** Where the recorded arch type came from, when the scan says. */
export function archTypeProvenance(scan) {
  if (scan.archTypeSource === 'ai') return 'derived from the plantar scan';
  if (scan.archTypeSource === 'manual') return 'selected by the clinician';
  return null;
}

/**
 * The AI vision model's own reading of the foot, kept apart from the recorded
 * classification. It used to be shown IN PLACE of the recorded arch and foot
 * type, so a clinician's correction never appeared on the report.
 */
export function visionReadingOf(scan) {
  const v = scan.archVisionAnalysis;
  const text = [v?.archHeight, v?.pronationStatus].filter(Boolean).join(' · ');
  return text || null;
}

/** Where the recorded foot type came from, when the scan says. */
export function footTypeProvenance(scan) {
  if (scan.footTypeSource === 'ai') return 'derived from the plantar scan';
  if (scan.footTypeSource === 'manual') return 'selected by the clinician';
  return null;
}

/** The patient profile fields this document actually carries. */
export function profileOf(scan) {
  const height = nonEmpty(scan.height) ? `${scan.height} cm` : null;
  const weight = nonEmpty(scan.weight) ? `${scan.weight} kg` : null;
  return [
    { label: 'Name', value: patientNameOf(scan) },
    { label: 'Age', value: nonEmpty(scan.age) ? `${scan.age} years` : null },
    { label: 'Gender', value: scan.gender },
    { label: 'Height', value: height },
    { label: 'Weight', value: weight },
    { label: 'Occupation', value: scan.occupation },
    { label: 'Lifestyle', value: scan.lifestyle },
    { label: 'Email', value: scan.email },
    { label: 'Phone', value: scan.phone },
  ];
}

/**
 * What the reader needs to know about how this document was produced.
 *
 * The kiosk records it because two of these are invisible otherwise and both
 * change how the page should be read. The built-in narrative is the important
 * one: it is a genuine feature — a clinic whose API key expired still gets a
 * report — but it reads exactly like a written assessment, and a patient is
 * entitled to know whether anything actually assessed them.
 *
 * Scans written before the record existed return nothing, which is correct:
 * absence of the field is not evidence of a fallback.
 */
export function generationNotes(scan) {
  const generation = scan?.generation;
  if (!generation) return [];

  const notes = [];

  if (generation.analysis?.source === 'fallback') {
    notes.push({
      id: 'fallback-narrative',
      tone: 'warn',
      text: 'The written sections of this report come from a standard template: the '
          + 'analysis service could not be reached when it was produced. Every '
          + 'measurement, angle and image above is unaffected.',
    });
  }

  const failed = generation.images?.failed ?? 0;
  if (failed > 0) {
    const total = failed + (generation.images?.uploaded ?? 0);
    notes.push({
      id: 'missing-images',
      tone: 'warn',
      text: `${failed} of ${total} images could not be saved and are missing from this `
          + 'report. The measurements taken from them are still shown.',
    });
  }

  if (generation.archVision === 'failed') {
    notes.push({
      id: 'arch-vision-failed',
      tone: 'info',
      text: 'The automated reading of the plantar photographs did not complete, so this '
          + 'report carries no per-foot commentary from it.',
    });
  }

  return notes;
}

/**
 * Whether this document is a finished report.
 *
 * A scan is written before its images are uploaded, so a run that failed leaves
 * one behind that has everything a report has except the pictures. `status` is
 * how the two are told apart; scans written before it existed have none, and
 * those are all completed runs.
 */
export function isPublished(scan) {
  return scan?.status !== 'generating' && scan?.status !== 'failed';
}

/**
 * Split a view's measurements into bilateral pairs and midline singles.
 *
 * The pipeline emits "Left calcaneal angle" and "Right calcaneal angle" as two
 * independent rows. A clinician reads them as one finding with two sides, and
 * the printed reference does too — one row per joint, a column per side, so the
 * asymmetry is visible without holding two numbers in your head.
 *
 * Anything with no Left/Right prefix is a midline measurement (trunk lean,
 * forward head, spinal deviation) and keeps its own row. Pairing those into a
 * two-column table would print an empty cell beside every one of them.
 */
export function pairAngles(angles) {
  const bilateral = new Map();
  const midline = [];

  for (const angle of angles ?? []) {
    if (!angle?.name) continue;
    const match = /^(Left|Right)\s+(.*)$/.exec(angle.name);
    if (!match) {
      midline.push(angle);
      continue;
    }
    const [, side, base] = match;
    const label = base.charAt(0).toUpperCase() + base.slice(1);
    const row = bilateral.get(label) ?? { label, right: null, left: null };
    row[side.toLowerCase()] = angle;
    bilateral.set(label, row);
  }

  return { bilateral: [...bilateral.values()], midline };
}

/**
 * The worse of a pair, for the row's single verdict.
 *
 * Reporting the better side would understate the finding, which on an
 * asymmetric rearfoot is the whole thing the assessment is for.
 */
export function worseOfPair(right, left) {
  const candidates = [right, left].filter(a => a && typeof a.degrees === 'number');
  if (!candidates.length) return null;
  return candidates.reduce((worst, a) =>
    severityRank(severityForAngle(a)) > severityRank(severityForAngle(worst)) ? a : worst);
}

